import { Search, TrendingUp, Sparkles, Loader2, CornerDownLeft } from 'lucide-react';
import { SearchResultRow } from './SearchResultRow';

const Heading = ({ children, isDarkMode }) => (
  <p className={`px-3 pt-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{children}</p>
);

export const PopularChips = ({ popular, isAr, isDarkMode, onQuery, testPrefix = 'search-popular' }) => (
  <div className="flex flex-wrap gap-2 px-3 pb-3" data-testid={`${testPrefix}-list`}>
    {popular.map((p, i) => (
      <button
        key={p.query}
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onQuery(p.query)}
        data-testid={`${testPrefix}-chip-${i}`}
        style={{ animationDelay: `${i * 40}ms` }}
        className={`animate-fadeIn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ${
          isDarkMode
            ? 'bg-[#F5A962]/10 border-[#F5A962]/25 text-[#F5C38E] hover:bg-[#F5A962]/20'
            : 'bg-[#F5A962]/10 border-[#F5A962]/30 text-[#B45309] hover:bg-[#F5A962]/20'
        }`}
      >
        <TrendingUp className="h-3 w-3" />
        <span dir="auto">{p.query}</span>
      </button>
    ))}
  </div>
);

/** Dropdown / overlay body: popular searches, live suggestions, "see all", and the AI fallback. */
export const SearchPanel = ({ box, isAr, isDarkMode }) => {
  const { q, items, queries, total, loading, popular, active, noResults, goSearch, pick, askAI } = box;
  const term = q.trim();
  const optionCls = (isActive) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start text-sm font-semibold transition-colors duration-150 ${
    isActive ? (isDarkMode ? 'bg-[#1E9BD9]/15 text-white' : 'bg-[#1E9BD9]/10 text-slate-900') : (isDarkMode ? 'text-slate-200 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100')
  }`;

  if (!term) {
    return popular.length ? (
      <div data-testid="search-panel-popular">
        <Heading isDarkMode={isDarkMode}>{isAr ? 'الأكثر بحثاً' : 'Most searched'}</Heading>
        <PopularChips popular={popular} isAr={isAr} isDarkMode={isDarkMode} onQuery={(text) => goSearch(text, 'popular_chip')} />
      </div>
    ) : null;
  }

  return (
    <div className="p-1.5" data-testid="search-panel-results">
      {items.length > 0 && <Heading isDarkMode={isDarkMode}>{isAr ? `نتائج سريعة (${total})` : `Quick results (${total})`}</Heading>}
      {items.map((item, i) => (
        <SearchResultRow key={`${item.section}-${item.id}`} item={item} active={active === i} isAr={isAr} isDarkMode={isDarkMode}
          onSelect={() => pick(item, i)} testId={`search-suggestion-${i}`} />
      ))}

      {queries.length > 0 && (
        <div className="px-1.5 pt-1">
          {queries.map((p, i) => (
            <button key={p.query} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => goSearch(p.query, 'popular_suggestion')}
              data-testid={`search-query-suggestion-${i}`} className={optionCls(false)}>
              <TrendingUp className="h-4 w-4 text-[#F5A962]" /><span dir="auto" className="truncate">{p.query}</span>
            </button>
          ))}
        </div>
      )}

      {noResults && (
        <div className={`mx-1.5 my-2 rounded-xl border px-3 py-3 text-xs ${isDarkMode ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`} data-testid="search-no-results">
          {isAr ? `ما لقينا نتائج لـ «${term}» في الموقع.` : `No results for “${term}” on the site.`}
        </div>
      )}

      <div className={`mt-1 pt-1 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => goSearch()} data-testid="search-see-all" className={optionCls(active === items.length)}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-[#1E9BD9]" /> : <Search className="h-4 w-4 text-[#1E9BD9]" />}
          <span className="truncate flex-1">{isAr ? `عرض كل النتائج لـ «${term}»` : `See all results for “${term}”`}</span>
          <CornerDownLeft className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
        </button>
        {noResults && (
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={askAI} data-testid="search-ask-ai" className={optionCls(active === items.length + 1)}>
            <Sparkles className="h-4 w-4 text-[#38BDF8]" />
            <span className="truncate">{isAr ? `اسأل المساعد الذكي عن «${term}»` : `Ask the AI assistant about “${term}”`}</span>
          </button>
        )}
      </div>
    </div>
  );
};
